import {MithrilComponent} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'
import {Icon} from '@bitstillery/common/components'
import {tip} from '@bitstillery/common/components'
import {content_props} from '@bitstillery/common/components/ui/tip/tip'
import m from '@bitstillery/mithril'
import type {Instance as TippyInstance, Props as TippyProps} from 'tippy.js'

interface ProcessStep {
    completed: boolean | (() => boolean)
    disabled?: boolean | (() => boolean)
    id: string
    /** Without one the step renders no link (an `onclick` step). */
    path?: string | (() => string)
    onclick?: () => void
    text: string
    tip?: string | (() => string | undefined)
    type?: string
    visible?: boolean
}

export interface ProcessStepperAttrs {
    className?: string
    data: {
        active: string | null
        current: number
    }
    process: ProcessStep[]
    variant?: 'vertical' | 'horizontal'
}

export class ProcessStepper extends MithrilComponent<ProcessStepperAttrs> {
    tippies: TippyInstance[] = []

    override oncreate(vnode: m.Vnode<ProcessStepperAttrs>) {
        for (const step of vnode.attrs.process) {
            if (!step.tip) {
                continue
            }

            let selector: Element | null

            selector = document.querySelector(`#tip-${step.id}`)
            const tip_config: Partial<TippyProps> = {
                allowHTML: true,
                animation: 'scale',
                appendTo: document.body,
                ...content_props(this.update_tip(step)),
                delay: 500,
                inertia: true,
                interactive: true,
                onTrigger: (instance: TippyInstance) => {
                    instance.setProps(content_props(this.update_tip(step)))
                },
                touch: 'hold',
                zIndex: 10_000_000_000_000_000,
            }

            // Without its element (rendered detached) tippy would answer an empty instance array.
            if (selector) {
                this.tippies.push(tip(selector, tip_config))
            }
        }
    }

    override onremove() {
        for (const tippy of this.tippies) {
            tippy.destroy()
        }
    }

    update_tip(step: ProcessStep) {
        if (typeof step.tip === 'function') {
            return step.tip()
        }
        return step.tip
    }

    view(vnode: m.Vnode<ProcessStepperAttrs>): m.Children {
        return (
            <div
                className={classes(
                    'c-process-stepper',
                    `variant-${vnode.attrs.variant ? vnode.attrs.variant : 'vertical'}`,
                    vnode.attrs.className,
                )}
            >
                {vnode.attrs.process.map((step, index) => {
                    const completed = typeof step.completed === 'function' ? step.completed() : step.completed
                    const disabled = typeof step.disabled === 'function' ? step.disabled() : step.disabled
                    if (step.id === vnode.attrs.data.active && vnode.attrs.data.current !== index) {
                        vnode.attrs.data.current = index
                    }
                    return (
                        <a
                            key={step.id}
                            id={`tip-${step.id}`}
                            href={
                                step.disabled || !step.path
                                    ? null
                                    : `${typeof step.path === 'function' ? step.path() : step.path}`
                            }
                            onclick={(e: MouseEvent) => {
                                if (vnode.attrs.data.active === step.id) {
                                    return
                                }

                                if (disabled) {
                                    e.preventDefault()
                                    return
                                }
                                if (typeof step.onclick === 'function') {
                                    step.onclick()
                                }
                            }}
                            className={classes('step', `type-${step.type ? step.type : 'default'}`, {
                                active: step.id === vnode.attrs.data.active,
                                completed,
                                disabled,
                                visible: 'visible' in step ? step.visible : true,
                            })}
                        >
                            <div className='title'>{step.text}</div>

                            {(() => {
                                if (completed) {
                                    return <Icon name='checked' type='unset' />
                                }
                                if (step.id === vnode.attrs.data.active) {
                                    return <Icon name='chevronRight' type='unset' />
                                }
                                if (step.type === 'danger') {
                                    return <Icon name='danger' type='unset' />
                                }
                                return <Icon name='chevronDown' type='unset' />
                            })()}
                        </a>
                    )
                })}
            </div>
        )
    }
}
